import { BEAD_SPECS } from '../domain/beads';
import { contrastInk, mixHex } from '../domain/color';
import { symbolForIndex } from '../domain/pattern';
import { STITCH_SPECS, buildLayout, physicalSize } from '../domain/stitches';
import { beadCounts, threadEstimate, totalBeads, wordChart } from '../domain/report';
import { EMPTY } from '../domain/types';
import type { Pattern } from '../domain/types';
import { downloadBlob, downloadText, safeFilename } from './format';

export interface ChartImageOptions {
  /** Width of one bead cell in pixels. */
  cellPx: number;
  showGrid: boolean;
  showSymbols: boolean;
  showNumbers: boolean;
  realistic: boolean;
  includeLegend: boolean;
  includeTitle: boolean;
  background: string;
  transparent: boolean;
}

export function defaultChartImageOptions(): ChartImageOptions {
  return {
    cellPx: 18,
    showGrid: true,
    showSymbols: false,
    showNumbers: true,
    realistic: false,
    includeLegend: true,
    includeTitle: true,
    background: '#ffffff',
    transparent: false,
  };
}

const MARGIN = 16;

export function renderPatternToCanvas(pattern: Pattern, opts: ChartImageOptions): HTMLCanvasElement {
  const layout = buildLayout(pattern.stitch, pattern.beadSize, pattern.width, pattern.height);
  const spec = BEAD_SPECS[pattern.beadSize];
  // cellPx describes the bead's width; everything else scales from the real
  // millimetre geometry so the printed chart has true bead proportions.
  const scale = opts.cellPx / layout.cellW;
  const chartW = layout.totalW * scale;
  const chartH = layout.totalH * scale;

  const gutter = opts.showNumbers ? Math.max(22, opts.cellPx * 1.4) : 0;
  const titleH = opts.includeTitle ? 46 : 0;

  const rows = opts.includeLegend ? beadCounts(pattern) : [];
  const legendRowH = 22;
  const legendH = opts.includeLegend ? 30 + rows.length * legendRowH : 0;

  const width = Math.ceil(MARGIN * 2 + gutter + chartW);
  const height = Math.ceil(MARGIN * 2 + titleH + gutter + chartH + legendH);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  const ctx = canvas.getContext('2d')!;

  if (!opts.transparent) {
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, width, height);
  }

  let y = MARGIN;
  const ink = opts.transparent ? '#111111' : contrastInk(opts.background);

  if (opts.includeTitle) {
    ctx.fillStyle = ink;
    ctx.font = '600 20px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(pattern.name, MARGIN, y);
    const size = physicalSize(pattern);
    ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = mixHex(ink, opts.background, 0.35);
    ctx.fillText(
      `${STITCH_SPECS[pattern.stitch].label} · ${pattern.width} × ${pattern.height} beads · ${spec.label} · ` +
        `${size.w.toFixed(1)} × ${size.h.toFixed(1)} mm`,
      MARGIN,
      y + 24,
    );
    y += titleH;
  }

  const originX = MARGIN + gutter;
  const originY = y + gutter;

  // Row and column numbers.
  if (opts.showNumbers) {
    ctx.fillStyle = mixHex(ink, opts.background, 0.25);
    ctx.font = `${Math.max(9, Math.min(13, opts.cellPx * 0.8))}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const step = opts.cellPx < 12 ? 10 : 5;
    // Skip a number that would print on top of its neighbour.
    let lastX = -Infinity;
    for (let col = 0; col < pattern.width; col++) {
      const n = col + 1;
      if (n !== 1 && n !== pattern.width && n % step !== 0) continue;
      const p = layout.pos(col, 0);
      const x = originX + (p.x + layout.cellW / 2) * scale;
      if (x - lastX < 16) continue;
      lastX = x;
      ctx.fillText(String(n), x, originY - gutter / 2);
    }
    let lastY = -Infinity;
    for (let row = pattern.height - 1; row >= 0; row--) {
      const n = pattern.height - row;
      if (n !== 1 && n !== pattern.height && n % step !== 0) continue;
      const p = layout.pos(0, row);
      const y = originY + (p.y + layout.cellH / 2) * scale;
      if (Math.abs(y - lastY) < 12) continue;
      lastY = y;
      ctx.fillText(String(n), originX - gutter / 2, y);
    }
  }

  const cw = layout.cellW * scale;
  const chh = layout.cellH * scale;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${Math.floor(Math.min(cw, chh) * 0.66)}px ui-sans-serif, system-ui, sans-serif`;

  for (let row = 0; row < pattern.height; row++) {
    for (let col = 0; col < pattern.width; col++) {
      const v = pattern.cells[row * pattern.width + col];
      const p = layout.pos(col, row);
      const x = originX + p.x * scale;
      const yy = originY + p.y * scale;
      if (v === EMPTY) {
        if (opts.showGrid) {
          ctx.strokeStyle = 'rgba(0,0,0,0.14)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x + 0.5, yy + 0.5, cw - 1, chh - 1);
        }
        continue;
      }
      const hex = pattern.palette[v]?.hex ?? '#cccccc';
      const radius = spec.shape === 'round' ? Math.min(cw, chh) / 2 : Math.min(cw, chh) * 0.28;
      ctx.beginPath();
      ctx.roundRect(x + 0.5, yy + 0.5, cw - 1, chh - 1, radius);
      if (opts.realistic) {
        const grad = ctx.createLinearGradient(x, yy, x, yy + chh);
        grad.addColorStop(0, mixHex(hex, '#ffffff', 0.32));
        grad.addColorStop(0.45, hex);
        grad.addColorStop(1, mixHex(hex, '#000000', 0.28));
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = hex;
      }
      ctx.fill();
      if (opts.showGrid) {
        ctx.strokeStyle = 'rgba(0,0,0,0.35)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      if (opts.showSymbols && Math.min(cw, chh) >= 9) {
        ctx.fillStyle = contrastInk(hex);
        ctx.fillText(symbolForIndex(v), x + cw / 2, yy + chh / 2 + 0.5);
      }
    }
  }

  if (opts.includeLegend) {
    let ly = originY + chartH + 24;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = '600 13px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = ink;
    ctx.fillText(`Beads used — ${totalBeads(pattern)} total`, MARGIN, ly);
    ly += 20;
    ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
    for (const row of rows) {
      ctx.fillStyle = row.color?.hex ?? '#999';
      ctx.beginPath();
      ctx.roundRect(MARGIN, ly - 7, 22, 14, 4);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
      if (opts.showSymbols) {
        ctx.fillStyle = contrastInk(row.color?.hex ?? '#999');
        ctx.textAlign = 'center';
        ctx.fillText(row.symbol, MARGIN + 11, ly);
        ctx.textAlign = 'left';
      }
      ctx.fillStyle = ink;
      const label = [row.color?.code, row.color?.name].filter(Boolean).join(' · ');
      ctx.fillText(`${label} — ${row.count} beads (${row.grams.toFixed(1)} g with spares)`, MARGIN + 32, ly);
      ly += legendRowH;
    }
  }

  return canvas;
}

export async function exportPng(pattern: Pattern, opts: ChartImageOptions): Promise<void> {
  const canvas = renderPatternToCanvas(pattern, opts);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('The browser could not produce a PNG');
  downloadBlob(`${safeFilename(pattern.name)}-chart.png`, blob);
}

/** Vector chart — prints at any size without going fuzzy. */
export function patternToSvg(pattern: Pattern, cellPx = 14): string {
  const layout = buildLayout(pattern.stitch, pattern.beadSize, pattern.width, pattern.height);
  const spec = BEAD_SPECS[pattern.beadSize];
  const scale = cellPx / layout.cellW;
  const w = layout.totalW * scale;
  const h = layout.totalH * scale;
  const cw = layout.cellW * scale;
  const ch = layout.cellH * scale;
  const r = spec.shape === 'round' ? Math.min(cw, ch) / 2 : Math.min(cw, ch) * 0.28;

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w.toFixed(1)}" height="${h.toFixed(1)}" viewBox="0 0 ${w.toFixed(1)} ${h.toFixed(1)}">`,
    `<title>${escapeXml(pattern.name)}</title>`,
    `<rect width="100%" height="100%" fill="#ffffff"/>`,
  ];
  for (let row = 0; row < pattern.height; row++) {
    for (let col = 0; col < pattern.width; col++) {
      const v = pattern.cells[row * pattern.width + col];
      const p = layout.pos(col, row);
      const x = (p.x * scale).toFixed(2);
      const y = (p.y * scale).toFixed(2);
      const fill = v === EMPTY ? 'none' : pattern.palette[v]?.hex ?? '#ccc';
      parts.push(
        `<rect x="${x}" y="${y}" width="${cw.toFixed(2)}" height="${ch.toFixed(2)}" rx="${r.toFixed(2)}" ` +
          `fill="${fill}" stroke="rgba(0,0,0,0.3)" stroke-width="0.6"/>`,
      );
    }
  }
  parts.push('</svg>');
  return parts.join('\n');
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] as string,
  );
}

/**
 * Open a print-ready page: chart, legend and word chart in one document.
 * Printing to PDF from there is the browser's job, which keeps this dependency
 * free and gives the user their own paper size and margins.
 */
/** The printable document: chart page, bead list page, word chart page. */
export function buildPrintHtml(pattern: Pattern, opts: Partial<ChartImageOptions> = {}): string {
  const chartOpts: ChartImageOptions = {
    ...defaultChartImageOptions(),
    cellPx: 16,
    ...opts,
    // The page prints its own heading and bead list, so the embedded chart
    // must not repeat them no matter what the export dialog is set to.
    includeLegend: false,
    includeTitle: false,
  };
  const dataUrl = renderPatternToCanvas(pattern, chartOpts).toDataURL('image/png');
  const rows = beadCounts(pattern);
  const size = physicalSize(pattern);
  const legendRows = rows
    .map(
      (r) => `<tr>
        <td><span class="sw" style="background:${r.color?.hex}"></span></td>
        <td class="sym">${escapeXml(r.symbol)}</td>
        <td>${escapeXml(r.color?.code ?? '')}</td>
        <td>${escapeXml(r.color?.name ?? '')}</td>
        <td class="num">${r.count}</td>
        <td class="num">${r.withSpares}</td>
        <td class="num">${r.grams.toFixed(2)} g</td>
      </tr>`,
    )
    .join('');

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeXml(pattern.name)}</title>
<style>
  body { font: 13px/1.5 ui-sans-serif, system-ui, -apple-system, sans-serif; color:#111; margin:24px; }
  h1 { font-size:20px; margin:0 0 2px; }
  .sub { color:#666; margin-bottom:16px; }
  img { max-width:100%; }
  table { border-collapse:collapse; margin-top:8px; font-size:12px; }
  th, td { border:1px solid #ddd; padding:4px 8px; text-align:left; }
  th { background:#f4f4f6; }
  td.num { text-align:right; font-variant-numeric:tabular-nums; }
  td.sym { text-align:center; }
  .sw { display:inline-block; width:22px; height:12px; border:1px solid #999; border-radius:3px; }
  pre { white-space:pre-wrap; font: 11px/1.45 ui-monospace, SFMono-Regular, monospace; background:#fafafa;
        border:1px solid #eee; padding:10px; border-radius:6px; }
  .note { color:#777; font-size:11px; margin-top:12px; }
  @media print { .page { page-break-after: always; } body { margin:12mm; } }
</style></head><body>
<div class="page">
  <h1>${escapeXml(pattern.name)}</h1>
  <div class="sub">${escapeXml(STITCH_SPECS[pattern.stitch].label)} ·
    ${pattern.width} × ${pattern.height} beads ·
    ${escapeXml(BEAD_SPECS[pattern.beadSize].label)} ·
    approx ${size.w.toFixed(1)} × ${size.h.toFixed(1)} mm ·
    ${totalBeads(pattern)} beads · ~${threadEstimate(pattern).toFixed(1)} m thread</div>
  <img src="${dataUrl}" alt="chart">
</div>
<div class="page">
  <h1>Bead list</h1>
  <table><thead><tr><th></th><th>Sym</th><th>Code</th><th>Colour</th><th>Beads</th><th>+ spares</th><th>Weight</th></tr></thead>
  <tbody>${legendRows}</tbody></table>
  <p class="note">Spares allow ~12%. Colour codes are approximate screen matches — check a physical colour card before ordering.</p>
</div>
<div>
  <h1>Word chart</h1>
  <pre>${escapeXml(wordChart(pattern, { useCodes: true, runLength: true }))}</pre>
</div>
</body></html>`;
}

/**
 * Open a print-ready page and send it to the printer. Saving it as a PDF is
 * then the browser's job, which keeps this dependency free and lets the user
 * pick their own paper size and margins.
 *
 * Returns 'printed' if the print window opened, or 'downloaded' if pop-ups were
 * blocked and the document was saved to a file instead — opening that file and
 * pressing print gets to the same place.
 */
export function openPrintView(
  pattern: Pattern,
  opts: Partial<ChartImageOptions> = {},
): 'printed' | 'downloaded' {
  const html = buildPrintHtml(pattern, opts);
  const win = window.open('', '_blank');
  if (!win) {
    downloadText(`${safeFilename(pattern.name)}-print.html`, html, 'text/html');
    return 'downloaded';
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 350);
  return 'printed';
}
